import { createAction, props } from '@ngrx/store';

import { ResourceData } from '../../elements/resource/resource.types';
import {
  AnimationInnerKey,
  AnimationKeys,
  BBox,
  GeneralShapeDescriptor,
  Translate,
  LiteralValue,
  SVGAttributes,
  ShapeIRI,
  ShapeTransform,
  AnimationValue,
  Scene,
  AnimationConfig,
  TextAnimationConfig,
  ContainerAnimationConfig,
  ContainerMenuItem,
  SceneBackgroundDefinition,
  ImageBgOptions,
  FileDescriptor,
  ProjectDescriptor,
} from '../../elements/resource/types/shape.type';
import { BulkUpdates, EditorState } from './reducer/editor.reducer';
import { StoryBoardItemDescriptor } from '../../services/canvas/story-board/story-board-item';
import { FontDescriptor } from '../control-panel/canvas-control-panel/text/font.service';
import { TextAnimationStep } from '../animation/text-animation.service';

export const EditorFeature = 'editor';

function createSetterAction<T>(name: string) {
  return createAction(EditorFeature + ':' + name, props<{ value: T }>());
}
export const setFileChanged = createSetterAction<boolean>('set-file-changed');

export const setCurrentProjectID = createAction(
  'editor:set-current-project-id',
  props<{ id: string }>(),
);

export const setBackgroundColor = createSetterAction<string>(
  'editor:set-background-color',
);

export const setOverlay =
  createSetterAction<EditorState['overlay']>('editor:set-overlay');

export const saveSceneRequestComplete = createAction(
  'editor:save-scene-request-complete',
);
export const appendComment = createAction(
  'editor:append-comment',
  props<{ text: string; time: number }>(),
);

export const setCommentDraft = createAction(
  'editor:set-comment-draft',
  props<{ commentIRI: string; text: string }>(),
);

export const clearCommentDraft = createAction(
  'editor:clear-comment-draft',
  props<{ commentIRI: string }>(),
);

export const setCanvasPosition = createAction(
  'editor:set-canvas-position',
  props<{
    x: number;
    y: number;
    scale: {
      x: number;
      y: number;
    };
  }>(),
);

export const setComponents = createAction(
  'editor:set-components',
  props<{
    components: Record<string, ResourceData>;
  }>(),
);

export const setFontDescriptor = createAction(
  'editor:set-font-descriptor',
  props<{
    key: string;
    descriptor: FontDescriptor;
  }>(),
);

export const addFile = createAction(
  'editor:add-file',
  props<{
    file: ResourceData;
  }>(),
);

export const toggleEditorState = createAction(
  'editor:toggle-editor-state',
  props<{
    key: 'panelOpened';
    value: any;
    force?: boolean;
  }>(),
);

export const saveResourceSuccess = createAction('editor:save-resource-success');

// export const setFiles = createAction(
//   'editor:set-files',
//   props<{ files: Record<string, ResourceData> }>(),
// );

export const createNewScene = createAction(
  'editor:save-scene-request',
  props<{
    scene: string;
    fileID: string;
    index: number;
  }>(),
);

export const assignSceneToFile = createAction(
  'editor:assign-scene-to-file',
  props<{
    mainFile: string;
    fileID: string;
    index: number;
  }>(),
);

export const saveSceneRequest = createAction(
  'editor:save-scene-request',
  props<{
    scene: string;
  }>(),
);

export const setLiteralValue = createAction(
  'editor:set-literal-value',
  props<{
    IRI: string;
    key: string;
    value: LiteralValue;
  }>(),
);
export const bulkUpdateFinish = createAction('editor:shape-update-finish');
export const clearBulkUpdates = createAction('editor:clear-bulk-updates');
export const setBulkUpdateItem = createAction(
  'editor:bulk-shape-update',
  props<{
    item?: {
      IRI: string;
      key: AnimationKeys;
      value: AnimationValue;
      innerKey?: AnimationInnerKey;
      base?: boolean;
    };
    items?: Array<{
      IRI: string;
      key: AnimationKeys;
      value: AnimationValue;
      innerKey?: AnimationInnerKey;
      base?: boolean;
    }>;
  }>(),
);

export const setExportIsInProgress = createSetterAction<boolean>(
  'editor:set-export-in-progress',
);

export const removeSceneRequest = createAction(
  'editor:remove-scene-request',
  props<{
    scene: string;
  }>(),
);

export const removeAllScenesRequest = createAction(
  'editor:remove-all-scene-request',
);

export const _setFileDescriptorItem = createAction(
  'editor:set-file-descriptor-key',
  props<{ key: keyof GeneralShapeDescriptor; value: any }>(),
);

export const addFileAction = createAction(
  'editor:add-file',
  props<{ name: string }>(),
);

export const setHiddenShape = createAction(
  'editor:set-hidden-shape',
  props<{ IRI: string; value: boolean }>(),
);

export const setLoadedScenes = createAction(
  'editor:set-loaded-scenes',
  props<{
    value: Record<string, boolean>;
  }>(),
);

export const addLoadedScene = createAction(
  'editor:add-loaded-scenes',
  props<{
    key: string;
  }>(),
);

export const loadFileAction = createAction(
  'editor:load-file',
  props<{
    ID: string;
    mode: 'main-file' | 'imported-shape' | 'subscene-load';
  }>(),
);
export const setHoveredShape = createAction(
  'editor:set-hovered-shape',
  props<{ IRI: string }>(),
);

export const setAnimationConfig = createAction(
  'editor:set-editor-config',
  props<{
    IRI?: string;
    baseType?: 'appear' | 'disappear';
    animationType?: 'fade' | 'mask';
    float?: AnimationConfig['float'];
    scale?: AnimationConfig['scale'];
    by?: TextAnimationConfig['by'];
    mode?: ContainerAnimationConfig['mode'];
  }>(),
);

export const setAnimationConfigBase = createAction(
  'editor:set-editor-config-base',
  props<{
    IRIs: string[];
    baseType: 'appear' | 'disappear';
    animationType: 'fade' | 'mask';
    // motion?: Pick<AnimationConfig, 'motion'>; //
    float?: AnimationConfig['float'];
    scale?: AnimationConfig['scale'];
    by?: TextAnimationConfig['by'];
    mode?: ContainerAnimationConfig['mode'];
  }>(),
);
export const setFiles = createAction(
  'editor:set-files',
  props<{
    files: Record<string, ResourceData>;
  }>(),
);

export const loadFileRequestAction = createAction(
  'editor:load-file-request',
  props<{
    ID: string;
    subSceneLoad?: boolean;
  }>(),
);

export const setSubsceneLoaded = createAction(
  'editor:set-sub-scene-loaded',
  props<{
    value: boolean;
  }>(),
);

export const resetBaseState = createAction('editor:reset-base-state');
export const setAttributeByState = createAction(
  'editor:set-attribute-state',
  props<{
    IRIs: string[];
    state: string;
    key?: AnimationKeys;
    innerKey?: AnimationInnerKey;
    value: AnimationValue;
    keys?: string[];
  }>(),
);

export const setShowComponentSearch = createAction(
  'editor:set-show-component-search',
  props<{
    value: boolean;
  }>(),
);

export const setCompleteCurrentFileDescriptor = createAction(
  'editor:set-complete-file-descriptor',
  props<{
    descriptor: FileDescriptor;
  }>(),
);

export const setFileDescriptor = createAction(
  'editor:set-file-descriptor',
  props<{
    IRI: string;
    key?: keyof FileDescriptor;
    descriptor: FileDescriptor | FileDescriptor[keyof FileDescriptor];
  }>(),
);

export const setCompleteProjectDescriptor = createAction(
  'editor:set-complete-project-descriptor',
  props<{
    descriptor: ProjectDescriptor;
  }>(),
);

export const loadCustomProject$ = createAction(
  'editor:load-custom-project',
  props<{
    projectID: string;
  }>(),
);

// -- // -- // -- //

export const setProjectDescriptor = createAction(
  'editor:set-project-descriptor',
  props<{
    IRI: string;
    key?: keyof ProjectDescriptor;
    descriptor: ProjectDescriptor | ProjectDescriptor[keyof ProjectDescriptor];
  }>(),
);

export const setChangeTookPlace = createAction('editor:set-change-took-place');

export const setFileLoading = createSetterAction<boolean>('set-file-loading');
export const setPreviewShapeLoading = createSetterAction<boolean>(
  'set-preview-shape-loading',
);

export const setEditorFile = createAction(
  'editor:set-show-component-search',
  props<{
    value: boolean;
  }>(),
);
export const saveChangedShapes = createAction(
  'editor:save-changed-shape',
  props<{
    IRI?: ShapeIRI;
    IRIs?: ShapeIRI[];
  }>(),
);

export const setcompleteShapeDescriptor = createAction(
  'editor:set-complete-shape-descriptor',
  props<{
    IRI: string;
    descriptor: GeneralShapeDescriptor;
  }>(),
);

export const setBaseShapeTransform = createAction(
  'editor:set-base-shape-transform',
  props<{
    IRI: ShapeIRI;
    transform: Partial<ShapeTransform>;
  }>(),
);

export const setShapeOriginalTransform = createAction(
  'editor:set-original-shape-transform',
  props<{
    IRI: ShapeIRI;
    transform: Partial<ShapeTransform>;
  }>(),
);

export const setShapeTranslateBase = createAction(
  'editor:set-shape-translate-base',
  props<{
    IRI: ShapeIRI;
    translate: Translate;
  }>(),
);

export const setCurrentShapeTranslate = createAction(
  'editor:set-current-shape-translate',
  props<{
    IRI: ShapeIRI;
    translate: Translate;
  }>(),
);

export const saveResourcesRequestAction = createAction(
  'editor:save-resources',
  props<{
    posts: ResourceData[];
    patches: ResourceData[];
    deletes: ResourceData[];
  }>(),
);

export const saveResourcesAction = createAction(
  'editor:save-resources-request',
);

export const setCanvasShapes = createAction(
  'editor:set-canvas-shapes',
  props<{
    file?: ResourceData;
    shapes: ResourceData[];
    files?: ResourceData[];
    merge?: boolean;
    currentFileID?: string;
  }>(),
);

export const openCanvasBgColorEdit = createAction(
  'editor:open-canvas-bg-color-edit',
);

export const setResource = createAction(
  'editor:set-resource',
  props<{ resource: ResourceData }>(),
);

export const setResources = createAction(
  'editor:set-resources',
  props<{ resources: Record<string, ResourceData> }>(),
);

export const setShapes = createAction(
  'editor:set-shapes',
  props<{
    shapes: Record<string, ResourceData>;
  }>(),
);

export const setShape = createAction(
  'editor:set-shape',
  props<{
    shape: ResourceData;
  }>(),
);

export const setShapeDescriptors = createAction(
  'editor:set-shape-descriptors',
  props<{
    shapes: ResourceData[];
  }>(),
);

export const addShapesAction = createAction(
  'editor:add-shapes-action',
  props<{
    file: ResourceData;
    merge?: boolean;
  }>(),
);

export const resetPatchedShapes = createAction('editor:reset-patched-shapes');

export const cleanFile = createAction('editor:clean-file');

export const setSelectedShapes = createAction(
  'editor:set-selected-shapes',
  props<{ shapes: Record<string, boolean> }>(),
);

export const selectShape = createAction(
  'editor:select-shape',
  props<{ IRI: string; shift?: boolean }>(),
);

export const deselectShape = createAction(
  'editor:deselect-shape',
  props<{ IRI: string }>(),
);

export const selectAllShapes = createAction('editor:select-all-shapes');
export const deselectAllShapes = createAction('editor:deselect-all-shapes');

// Scene selection (thumbnails)
export const setSelectedScene = createAction(
  'editor:set-selected-scene',
  props<{ IRI: string }>(),
);
export const resetSelectedScenes = createAction(
  'editor:reset-selected-scenes',
  props<{ IRI: string }>(),
);
export const clearSelectedScenes = createAction('editor:clear-selected-scenes');

export const setOriginalSVGAttributes = createAction(
  'editor:set-original-svg-attributes',
  props<{
    IRI: string;
    svgAttributes: SVGAttributes;
  }>(),
);

export const saveSubScenePosition = createAction(
  'editor:save-sub-scene-position',
  props<{
    scene: string;
    subScene: string;
    position: {
      x: number;
      y: number;
      scale: {
        x: number;
        y: number;
      };
    };
  }>(),
);
export const saveScenePosition = createAction(
  'editor:save-scene-position',
  props<{
    scene: string;
    position: {
      x: number;
      y: number;
      scale: {
        x: number;
        y: number;
      };
    };
  }>(),
);

export const updateDescriptorOfShapeBaseAction = createAction(
  'editor:update-descriptor-of-shape-base-action',
  props<{
    IRI: string;
    descriptor?: Partial<GeneralShapeDescriptor>;
    original?: boolean;
  }>(),
);

export const updateCurrentDescriptorBaseAction = createAction(
  'editor:update-current-descriptor-of-shape-base-action',
  props<{
    IRI: string;
    descriptor?: Partial<GeneralShapeDescriptor>;
  }>(),
);

export const setShapeToBeSaved = createAction(
  'editor:set-shape-to-be-saved',
  props<{ IRI: string }>(),
);

export const setCurrentSVGAttributesOfShape = createAction(
  'editor:set-current-svg-attribute-of-shape',
  props<{ shapeIRI: string; svgAttributes: SVGAttributes }>(),
);

export const setSVGAttributesOfShape = createAction(
  'editor:set-svg-attribute-of-shape',
  props<{ shapeIRI: string; svgAttributes: SVGAttributes }>(),
);

export const setSVGSubAttribute = createAction(
  'editor:set-svg-sub-attribute',
  props<{
    IRI?: string;
    IRIs?: string[];
    key: keyof SVGAttributes;
    innerKey: AnimationInnerKey;
    value: string | number | boolean;
  }>(),
);

export const setCurrentSVGSubAttribute = createAction(
  'editor:set-svg-current-sub-attribute',
  props<{
    IRI: string;
    key: keyof SVGAttributes;
    innerKey: AnimationInnerKey;
    value: string | number | boolean;
  }>(),
);

export const setCurrentScenes = createAction(
  'editor:set-current-scenes',
  props<{
    scenes: string[];
  }>(),
);

export const selectSubScene = createAction(
  'editor:select-sub-scene',
  props<{
    name: string;
  }>(),
);

export const selectSubSceneBase = createAction(
  'editor:select-sub-scene-base',
  props<{
    name: string;
  }>(),
);

export const setCurrentScene = createAction(
  'editor:set-current-scene',
  props<{
    scene: string;
  }>(),
);

export const addNewSceneBase = createAction(
  'editor:add-new-scene-base',
  props<{
    name: string;
  }>(),
);

export const addSubScene = createAction(
  'editor:add-sub-scene',
  props<{
    parentIndex: number;
    name: string;
    index: number;
  }>(),
);

export const openFile = createAction(
  'editor:open-file',
  props<{
    IRI: string;
    importedShape?: boolean;
  }>(),
);

export const deleteComponent = createAction(
  'editor:delete-component',
  props<{
    IRI?: string;
  }>(),
);

export const addFileToStore = createAction(
  'editor:add-file-to-store',
  props<{
    file: ResourceData;
    shapes: Record<string, ResourceData>;
  }>(),
);

export const setSubSceneName = createAction(
  'editor:set-sub-scene-name',
  props<{
    parentIndex: number;
    index: number;
    name: string;
  }>(),
);

export const setScenes = createAction(
  'editor:set-scenes',
  props<{
    scenes: Scene[];
  }>(),
);

export const setSubScenePosition = createAction(
  'editor:add-sub-scene',
  props<{
    parentIndex: number;
    name: string;
    index: number;
  }>(),
);

export const setTransformSubAttribute = createAction(
  'editor:set-transform-sub-attribute',
  props<{
    IRI?: string;
    IRIs?: string[];
    key: keyof ShapeTransform;
    innerKey: 'x' | 'y' | 'relative';
    value: number | boolean;
  }>(),
);

export const generateImage = createAction(
  'editor:generate-image',
  props<{
    prompt: string;
    style?: string;
    imageFile?: File;
  }>(),
);

export const generateImageSuccess = createAction(
  'editor:generate-image-success',
  props<{
    imageBlob: Blob;
  }>(),
);

export const generateImageFailure = createAction(
  'editor:generate-image-failure',
);

export const setProjectInput = createAction(
  'editor:set-project-input',
  props<{
    project: ResourceData;
    files: Record<string, ResourceData>;
  }>(),
);

export const setProjectPreviewIsLoading = createAction(
  'editor:set-project-preview-is-loading',
  props<{
    value: boolean;
  }>(),
);

export const loadProjectPreview = createAction(
  'editor:load-project-input',
  props<{
    projectID: string;
  }>(),
);

export const loadProjectPreviewRequest = createAction(
  'editor:load-project-input-request',
  props<{
    projectID: string;
  }>(),
);

export const setNewProjectName = createAction(
  'editor:set-new-project-name',
  props<{
    name: string;
  }>(),
);
// New project prompts (support grouping text + many files) //
export type PromptMediaFile = {
  type: 'image' | 'video' | 'doc';
  url: string;
  name?: string;
  docType?: 'pdf' | 'ppt';
  durationSec?: number;
};
export type PromptItem = {
  text?: string;
  files?: PromptMediaFile[];
};
export const addPrompt = createAction(
  'editor:add-prompt',
  props<{ prompt: PromptItem }>(),
);
export const generateVideo = createAction(
  'editor:generate-video',
  props<{ prompt: string }>(),
);

// Colors management for New Project
export const setColors = createAction(
  'editor:set-colors',
  props<{ colors: string[] }>(),
);
export const addColor = createAction(
  'editor:add-color',
  props<{ color: string }>(),
);
export const removeColor = createAction(
  'editor:remove-color',
  props<{ color: string }>(),
);
export const updateColor = createAction(
  'editor:update-color',
  props<{ index: number; color: string }>(),
);
export const reorderColors = createAction(
  'editor:reorder-colors',
  props<{ previousIndex: number; currentIndex: number }>(),
);
export const clearColors = createAction('editor:clear-colors');

export const deleteShapeFailure = createAction('editor:delete-shape-failure');

export const clearImagePrompt = createAction('editor:clear-image-prompt');
export const setImagePromptText = createAction(
  'editor:set-image-prompt-text',
  props<{
    text: string;
  }>(),
);

export const setImagePrompt = createAction(
  'editor:set-image-prompt',
  props<{
    prompt: string;
    imageIRI?: string;
  }>(),
);

export const requestImagePrompt = createAction(
  'editor:request-image-prompt',
  props<{
    prompt: string;
    IRI?: string;
  }>(),
);

export const setDescriptorSubAttribute = createAction(
  'editor:set-descriptor-sub-attribute',
  props<{
    shapeIRI: string;
    key: 'sections' | 'index' | 'label' | 'opacity';
    innerKey?: AnimationInnerKey;
    value: string | number | boolean;
  }>(),
);

export const setCurrentDescriptorSubAttribute = createAction(
  'editor:set-current-descriptor-sub-attribute',
  props<{
    shapeIRI: string;
    key: 'sections' | 'index' | 'label' | 'opacity';
    innerKey?: AnimationInnerKey;
    value: string | number | boolean;
  }>(),
);

export const setSaveLoading = createAction(
  'editor:set-save-loading',
  props<{
    value: boolean;
  }>(),
);

export const addNewState = createAction(
  'editor:add-new-state',
  props<{
    name: string;
    index?: number;
  }>(),
);

export const addToFileVisitStack = createAction(
  'editor:add-to-visit-stack',
  props<{
    ID: string;
  }>(),
);

export const popVisitStack = createAction('editor:pop-visit-stack');
export const moveBack = createAction('editor:move-back-on-stack');
export const removeState = createAction(
  'editor:remove-state',
  props<{
    name: string;
  }>(),
);

export const setCurrentState = createAction(
  'editor:set-current-state',
  props<{
    name: string;
  }>(),
);

export const addNewShapeAction = createAction(
  'editor:add-new-shape',
  props<{
    data: ResourceData;
  }>(),
);

export const setNewShapeAction = createAction(
  'editor:set-new-shape',
  props<{
    data: ResourceData;
  }>(),
);

export const setShapeLabel = createAction(
  'editor:set-shape-label',
  props<{ IRI: string; label: string }>(),
);

export const toggleSelectAction = createAction(
  'editor:toggle-select-action',
  props<{ IRI: string }>(),
);

export const setShapeDescriptor = createAction(
  'editor:set-descriptor',
  props<{ IRI: string; descriptor: GeneralShapeDescriptor }>(),
);

export const setCurrentDescriptor = createAction(
  'editor:set-current-descriptor',
  props<{ IRI: string; descriptor: GeneralShapeDescriptor }>(),
);

export const setOriginalDescriptor = createAction(
  'editor:set-original-descriptor',
  props<{ IRI: string; descriptor: GeneralShapeDescriptor }>(),
);

export const addShapeToStore = createAction(
  'editor:add-shape-to-store',
  props<{ data: ResourceData<GeneralShapeDescriptor> }>(),
);

export const setResourcePatch = createAction(
  'editor:set-shape-resource-patch',
  props<{
    IRI?: string;
    IRIs?: string[];
    field: 'literals' | 'relationships';
    key: string;
  }>(),
);

export const setResourcePost = createAction(
  'editor:set-shape-resource-post',
  props<{
    IRI?: string;
    IRIs?: string[];
    resourceType: 'nw:Shape' | 'nw:File';
  }>(),
);

export const setResourceDelete = createAction(
  'editor:set-resource-delete',
  props<{
    IRI?: string;
    IRIs?: string[];
    resourceType: 'nw:Shape' | 'nw:File';
  }>(),
);

export const saveFileRequestComplete = createAction(
  'editor:save-file-request-complete',
);

export const setBBoxOfShape = createAction(
  'editor:set-bbox-of-shape',
  props<{ IRI: string; bBox: BBox }>(),
);

export const setFile = createAction(
  'editor:set-file',
  props<{
    file: ResourceData;
  }>(),
);

export const setRelationship = createAction(
  'editor:set-relationship',
  props<{
    subject: string;
    relationship: string;
    object?: string;
    objects?: string[];
  }>(),
);

export const setRelationships = createAction(
  'editor:set-relationships',
  props<{
    IRI: string;
    relationships: Record<string, string>;
  }>(),
);

export const incrementRadius = createAction(
  'editor:increment-radius',
  props<{
    increment: number;
  }>(),
);

export const setRelationshipBase = createAction(
  'editor:set-relationship-base',
  props<{
    subject: string;
    subjectType?: 'nw:Shape' | 'nw:File';
    relationship: string;
    object: string;
    oneToMany?: boolean;
  }>(),
);

export const _setRelationshipBase = createAction(
  'editor:set-relationship-base-new',
  props<{
    subject: string;
    relationship: string;
    object: string;
    save?: boolean;
  }>(),
);

export const setContainerMenuItem = createAction(
  'editor:set-container-menu-item',
  props<{ IRI: string; menu: ContainerMenuItem }>(),
);

export const setContainerMenuItemState = createAction(
  'editor:set-container-menu-item-state',
  props<{ IRI: string; state: 'hidden' | 'hover' | 'active' }>(),
);

export const setContainerItemAction = createAction(
  'editor:set-container-menu-item-action',
  props<{ IRI: string; action: 'mouseenter' | 'mouseleave' | 'click' }>(),
);

export const removeContainerMenuItem = createAction(
  'editor:remove-container-menu',
  props<{ IRI: string }>(),
);

export const clearContainerMenu = createAction('editor:clear-container-menu');

export const removeRelationshipBase = createAction(
  'editor:remove-relationship-base',
  props<{
    fileIRI: string;
    shapeIRI?: string;
    relationship: string;
    objectIRI?: string;
  }>(),
);

export const setSelectedStoryBoardItem = createAction(
  'editor:set-selected-story-board-item',
  props<{ id: number }>(),
);

export const setStoryBoardItem = createAction(
  'editor:set-story-board-item',
  props<{ item: StoryBoardItemDescriptor }>(),
);

export const editStoryBoardNote = createAction(
  'editor:edit-story-board-note',
  props<{ text: string; index: number }>(),
);

export const fetchShape = createAction(
  'editor:fetch-shape-action',
  props<{ IRI: string }>(),
);

export const addState = createAction(
  'editor:add-state',
  props<{ name: string }>(),
);

export const selectState = createAction(
  'editor:select-state',
  props<{ name: string }>(),
);

export const setNoAnimationMode = createAction(
  'editor:set-no-animation-mode',
  props<{
    value: boolean;
  }>(),
);

export const setFontLoaded = createAction(
  'editor:set-font-loaded',
  props<{
    value: boolean;
  }>(),
);

export const createNewComponentAction = createAction(
  'editor:create-new-component',
  props<{
    IRI: string;
    label: string;
    descriptor: any;
  }>(),
);

export const saveNewComponentRequestAction = createAction(
  'editor:save-new-comp-request',
  props<{
    data: ResourceData;
  }>(),
);

export const setResourceLabel = createAction(
  'editor:set-resource-label',
  props<{
    IRI: string;
    label: string;
  }>(),
);

export const setResourceLabelBase = createAction(
  'editor:set-resource-label-base',
  props<{
    IRI: string;
    label: string;
  }>(),
);

export const setBackgroundSegment = createAction(
  'editor:set-background-segment',
  props<{
    segment: SceneBackgroundDefinition;
  }>(),
);

export const setBackgroundDefinition = createAction(
  'editor:set-background-definition',
  props<{
    id?: string;
    definition: SceneBackgroundDefinition;
  }>(),
);

export const insertScene = createAction(
  'editor:insert-scene',
  props<{
    data: ResourceData;
  }>(),
);

export const deleteScene = createAction(
  'editor:delete-scene',
  props<{
    IRI: string;
  }>(),
);

export const setFileDescriptorItem = createAction(
  'editor:set-file-descriptor-item',
  props<{
    key: keyof FileDescriptor;
    value: any;
  }>(),
);

export const setCurrentTextAnimationStep = createAction(
  'editor:set-current-text-animation-step',
  props<{
    step: TextAnimationStep;
  }>(),
);

export const saveProjectDescriptorByKey = createAction(
  'editor:save-project-descriptor-by-key',
  props<{
    key: keyof ProjectDescriptor;
    value: any;
  }>(),
);
